import axios, { AxiosError, AxiosProgressEvent, GenericAbortSignal, Method, ParamsSerializerOptions } from 'axios';

import { SERVER_ERROR_MESSAGE } from '@/shared/api/errors';

const GENERIC_AXIOS_ERRORS = {
  badOptionError: 'ERR_BAD_OPTION',
  badOptionValueError: 'ERR_BAD_OPTION_VALUE',
  badRequestError: 'ERR_BAD_REQUEST',
  badResponseError: 'ERR_BAD_RESPONSE',
  connAbortedError: 'ECONNABORTED',
  deprecatedError: 'ERR_DEPRECATED',
  invalidURLError: 'ERR_INVALID_URL',
  networkError: 'ERR_NETWORK',
  notSupportError: 'ERR_NOT_SUPPORT',
  timeoutError: 'ETIMEDOUT',
  tooManyRedirectsError: 'ERR_FR_TOO_MANY_REDIRECTS',
  canceledError: 'ERR_CANCELED',
} as const;

type TRequestClosure<ResponseData, Params extends object = object, Data = object> = {
  adapter?: (data: ResponseData) => ResponseData;
  data?: Data;
  params?: Params;
  paramsSerializer?: ParamsSerializerOptions;
  signal?: GenericAbortSignal;
  type?: string;
  onUploadProgress?: (progressEvent: AxiosProgressEvent) => void;
};

type Kind = 'auth' | 'public';

const awaitWithAbort = <T>(operation: () => Promise<T>, signal?: GenericAbortSignal): Promise<T> => {
  if (!signal) return operation();
  if (signal.aborted) return Promise.reject(new Error('Request aborted'));
  return new Promise<T>((resolve, reject) => {
    const onAbort = () => {
      signal.removeEventListener?.('abort', onAbort);
      reject(new Error('Request aborted'));
    };
    signal.addEventListener?.('abort', onAbort);
    operation().then(
      (value) => {
        signal.removeEventListener?.('abort', onAbort);
        resolve(value);
      },
      (error) => {
        signal.removeEventListener?.('abort', onAbort);
        reject(error);
      },
    );
  });
};

const requestClosure =
  (method: Method, kind: Kind) =>
  <ResponseData, Params extends object = object, Data = object>(
    url: string,
    {
      adapter,
      data,
      params,
      paramsSerializer,
      signal,
      type = 'application/json',
      onUploadProgress,
    }: TRequestClosure<ResponseData, Params, Data> = {},
  ): Promise<ResponseData> =>
    new Promise<ResponseData>(async (resolve, reject) => {
      const headers: Record<string, string> = {};

      if (method === 'post' || method === 'put' || method === 'patch') {
        headers['Content-Type'] = type;
      }

      if (kind === 'auth') {
        let token: string;
        try {
          // Dynamic import so Vitest's vi.mock('./getAuthToken') is honored;
          // the static-import reference is captured too early by the test setup chain.
          token = await awaitWithAbort(async () => {
            const { getAuthToken } = await import('./getAuthToken');
            return getAuthToken();
          }, signal);
        } catch {
          reject({ data: null, status: 0, statusText: signal?.aborted ? 'Request aborted' : 'Session invalidated' });
          return;
        }
        headers.authorization = `Bearer ${token}`;
      }

      axios
        .request<ResponseData>({
          data,
          headers,
          method,
          params,
          paramsSerializer,
          signal,
          url,
          onUploadProgress,
        })
        .then((response) => adapter?.(response.data) ?? response.data)
        .then(resolve)
        .catch((error: AxiosError) => {
          if (error.response) {
            const { data: errorPayload, status, statusText } = error.response;
            reject({ data: errorPayload, status, statusText });
            return;
          }

          if (!error.response && error.request && error.code === GENERIC_AXIOS_ERRORS.networkError) {
            reject({ data: null, status: 504, statusText: 'Gateway Timeout' });
            return;
          }

          // Подстраховка: мы не ожидаем, что попадаем в этот reject
          // когда-либо, но если это случится то выбросим стандартную ошибку
          // и showErrorAlert успешно обработает ее
          reject(new Error(SERVER_ERROR_MESSAGE));
        });
    });

// Authenticated request methods (default — send token, attach session signal)
export const post = requestClosure('post', 'auth');
export const get = requestClosure('get', 'auth');
export const put = requestClosure('put', 'auth');
export const patch = requestClosure('patch', 'auth');
export const del = requestClosure('delete', 'auth');

// Public request methods (never send token, no session signal)
export const postPublic = requestClosure('post', 'public');
export const getPublic = requestClosure('get', 'public');
export const putPublic = requestClosure('put', 'public');
export const patchPublic = requestClosure('patch', 'public');
export const delPublic = requestClosure('delete', 'public');
